import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreateService, useUpdateService, RECURRENCE_LABEL, RecurrencePeriod, ServiceRecord } from "@/api/services";
import { getApiErrorMessage } from "@/api/client";

const RECURRENCE_OPTIONS = Object.keys(RECURRENCE_LABEL) as RecurrencePeriod[];

export default function ServiceFormModal({ service, onClose }: { service?: ServiceRecord; onClose: () => void }) {
  const createService = useCreateService();
  const updateService = useUpdateService();
  const isEditing = !!service;

  const [name, setName] = useState(service?.name ?? "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [recurrence, setRecurrence] = useState<RecurrencePeriod>(service?.recurrence ?? "MENSUAL");
  const [defaultPrice, setDefaultPrice] = useState(service?.defaultPrice ?? "");
  const [isActive, setIsActive] = useState(service?.isActive ?? true);

  const isPending = createService.isPending || updateService.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await updateService.mutateAsync({
          id: service.id,
          name,
          description: description || undefined,
          recurrence,
          defaultPrice: defaultPrice || undefined,
          isActive,
        });
        toast.success("Servicio actualizado correctamente.");
      } else {
        await createService.mutateAsync({
          name,
          description: description || undefined,
          recurrence,
          defaultPrice: defaultPrice || undefined,
        });
        toast.success("Servicio agregado al catálogo.");
      }
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-sm p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{isEditing ? "Editar servicio" : "Nuevo servicio"}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Descripción</label>
            <textarea className="input-field mt-1" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Recurrencia</label>
              <select className="input-field mt-1" value={recurrence} onChange={(e) => setRecurrence(e.target.value as RecurrencePeriod)}>
                {RECURRENCE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {RECURRENCE_LABEL[r]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Precio base (COP)</label>
              <input
                type="number"
                min="0"
                className="input-field mt-1"
                value={defaultPrice}
                onChange={(e) => setDefaultPrice(e.target.value)}
              />
            </div>
          </div>
          {isEditing && (
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Servicio activo (disponible para contratar)
            </label>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={isPending}>
              {isPending ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear servicio"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
